import { useEffect } from 'react';
import { config } from '../config';
import { useAuth } from '../hooks/useAuth';
import { Helmet } from 'react-helmet';

interface GoogleSignInButtonProps {
  onSuccess: () => void;
}

declare global {
  interface Window {
    handleGoogleSignIn: (response: any) => void;
  }
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onSuccess
}) => {
  useEffect(() => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      console.error('Google Client ID is not configured');
      return;
    }

    window.handleGoogleSignIn = async (response) => {
      try {
        const result = await fetch(`${config.API_URL}/auth/google/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: `credential=${response.credential}`,
          credentials: 'include',
          mode: 'cors'
        });

        if (!result.ok) {
          const errorData = await result.json();
          console.error('Google authentication failed:', errorData.message);
          throw new Error(errorData.message || 'Google authentication failed');
        }

        const data = await result.json();
        console.log('Google sign-in successful:', data);
        onSuccess();
      } catch (error) {
        console.error('Google authentication failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
        throw new Error(errorMessage);
      }
    };

    console.log('Initializing Google Sign-In with client ID:', process.env.REACT_APP_GOOGLE_CLIENT_ID);

    const script = document.createElement('script');
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onerror = (error) => {
      console.error('Failed to load Google Sign-In script:', error);
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [onSuccess]);

  return (
    <>
      <Helmet>
        <meta name="referrer" content="strict-origin-when-cross-origin" />
      </Helmet>
      <div className="w-full max-w-[250px]">
        <div
          id="g_id_onload"
          data-client_id={process.env.REACT_APP_GOOGLE_CLIENT_ID}
          data-callback="handleGoogleSignIn"
          data-itp_support="true"
          data-auto_prompt="false"
          data-context="signin"
        />
        <div
          className="g_id_signin"
          data-type="standard"
          data-size="large"
          data-theme="outline"
          data-text="sign_in_with"
          data-shape="rectangular"
          data-logo_alignment="left"
          data-width="250"
        />
      </div>
    </>
  );
}; 